/**
 * Wheel-matching helper for category-tree's leaf-click flow.
 *
 * PR #3167 iter-19 (codex CR-2 MINOR): extracted from category-tree/
 * index.tsx:148 to enable focused unit testing of the match priority +
 * fallback logic. The logic is behaviorally important (wrong-wheel
 * selection on same-leaf siblings is a real category-disambiguation bug
 * — see iter-6 BLOCKING-2 / iter-9 / iter-10 history on PR #3167) and
 * was previously untested per [[checks-vs-enforcement]].
 *
 * Match priority:
 *   1. EXACT category_path match against canonical disambiguator.
 *   2. Fallback: leaf-only center_term match, narrowed to legacy
 *      pre-canonical rows (category_path IS NULL) when categoryPath
 *      argument is provided. ONLY when there's EXACTLY ONE such
 *      candidate of wheel_type='category' (ambiguous or non-null
 *      sibling → return null; user prompted to generate a fresh
 *      canonical-keyed wheel).
 *
 * PR #3167 iter-21 (codex CR-2 BLOCKING): the legacy fallback when
 * `categoryPath` IS provided is restricted to NULL-category_path rows
 * ONLY. Pre-iter-21 the fallback also matched non-null SIBLING rows
 * with the same leaf — defeating the PR's core same-leaf-sibling
 * fix. Example: incoming request for `Women > Shoes` (no exact row);
 * existing wheel `Men > Shoes` with non-null category_path matched
 * leaf 'shoes' → loaded WRONG wheel. Post-iter-21: legacy fallback
 * fires only when the candidate row has category_path IS NULL (true
 * pre-canonical legacy data; non-null is a distinct canonical wheel
 * that the user must NOT silently load).
 *
 * When `categoryPath` is NOT provided (e.g. leaf clicked from a tree
 * view that doesn't carry canonical context), the broader fallback
 * applies (any wheel_type='category' candidate, regardless of NULL
 * vs non-null) — there's no canonical context to disambiguate against.
 *
 * Sector/product enhanced wheels share center_term with category leaves
 * but represent a different concern; the fallback intentionally narrows
 * to wheel_type='category' to avoid cross-wheel-class leakage. NOTE:
 * this also excludes Pipeline-B Enhanced wheels generated in 'category'
 * mode (wheel_type='enhanced' + metadata.mode='category'). For wheels
 * with non-null category_path the canonical-path match path (priority 1)
 * still works regardless of wheel_type. The exclusion only affects the
 * legacy fallback for pre-iter-7 Pipeline-B wheels that have NULL
 * category_path — those will appear as "no wheel" and prompt
 * regeneration. This is INTENTIONAL for iter-21 scope (codex CR-1
 * MINOR documented; broader fix tracked as follow-up enhancement to
 * project metadata.mode through the list endpoint per WheelHistory
 * interface; PR-body discussion).
 */
export interface WheelCandidate {
  id?: string;
  center_term?: string;
  category_path?: string | null;
  wheel_type?: string;
  [key: string]: unknown;
}

export function findExistingWheelForCategory(
  wheels: WheelCandidate[],
  categoryName: string,
  categoryPath?: string,
): WheelCandidate | null {
  // Priority 1: exact canonical path match
  if (categoryPath) {
    const exact = wheels.find(
      (w) => typeof w.category_path === 'string' && w.category_path === categoryPath,
    );
    if (exact) return exact;
  }

  // Priority 2: leaf-only fallback restricted to wheel_type='category'.
  //
  // PR #3167 iter-21 CR-2 BLOCKING: when categoryPath IS provided, the
  // fallback is FURTHER narrowed to legacy rows (category_path IS NULL).
  // Non-null category_path on a candidate row means "a canonical wheel
  // exists for a DIFFERENT path" — silently loading that defeats the
  // same-leaf-sibling fix. When categoryPath is NOT provided, no
  // canonical context exists to disambiguate, so the broader fallback
  // applies (any category-typed wheel with matching leaf).
  const leafCandidates = wheels.filter(
    (w) => typeof w.center_term === 'string'
      && w.center_term.toLowerCase() === categoryName.toLowerCase()
      && w.wheel_type === 'category',
  );
  const fallbackCandidates = categoryPath
    ? leafCandidates.filter((w) => w.category_path === null || w.category_path === undefined)
    : leafCandidates;
  if (fallbackCandidates.length === 1) {
    return fallbackCandidates[0];
  }

  return null;
}
